import React, { useEffect, useRef, useState } from "react";

import { Button, IconButton, TextField } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";

import SideNav from "component/layouts/sideNav";
import {
  closureStatusRenderer,
  getProjectManagers,
  getSites,
  getSupplierCategories,
  getSuppliers,
  projectManagerCellRenderer,
  setProjectManagers,
  setSites,
  setSupplierCategories,
  setSuppliers,
  setUsers,
  siteCellRenderer,
  supplierCellRenderer,
  userCellRenderer,
} from "component/cellRenderers/renderers";
import {
  setAlertConfirmDeletion,
  setAlertTitleText,
} from "component/modal/alertModal";

import {
  ADD,
  DELETE,
  GO,
  LABOUR_MANAGEMENT,
  SUB_CONTRACTOR_ADVANCE_REQUEST,
} from "resources/constant/strings/string";
import {
  LIGHT_GREY_BACKGROUND,
  PRUSSIAN_BLUE,
  SPANISH_GREEN,
  WHITE,
} from "resources/constant/colors/theme";
import {
  PATH_LOGIN,
  PATH_SUB_CONTRACTOR_ADVANCE_REQUEST,
  PATH_SUB_CONTRACTOR_ADVANCE_REQUEST_DETAIL,
} from "resources/constant/url/url";

import { APILOGIN } from "apihandler/login";
import { APIMASTER } from "apihandler/master";
import { APILABOURMGMT } from "apihandler/labourmgmt";

import { setDates, setFilterModel } from "store/Slice/subconAdvanceSlice";


export default function SubContractorAdvanceRequest() {
  const { date } = useParams();
  const navigate = useNavigate();
  const gridRef = useRef();

  const dispatch = useDispatch();
  const { fromDate, toDate, filterModel } = useSelector((s) => s.subconAdvance);

  const [dateValue, setDateValue] = useState(null);
  const [toDateValue, setToDateValue] = useState(dayjs().endOf("month"));

  useEffect(() => {
    if (!APILOGIN.GetIsLoggedIn()) navigate(PATH_LOGIN);
  }, []);

  const [selectedRowId, setSelectedRowId] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const deleteModel = () => {
    if (selectedRowIds.length === 0) return;

    setAlertConfirmDeletion(
      "Confirmation needed",
      `Do you confirm on proceeding to delete ${selectedRowIds.length} record(s)?`
    ).then((consent) => {
      if (!consent.isConfirmed) return;

      console.log("delete the selected ids: ", selectedRowIds);
      APILABOURMGMT.DeleteSubContractorAdvances(selectedRowIds)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.statusCode === 204) {
            const selectedData = gridRef.current.api.getSelectedRows();
            gridRef.current.api.applyTransaction({ remove: selectedData });
            setAlertTitleText("Success", "The record was deleted successfully");
          } else {
            setAlertTitleText(
              "Oops...",
              `Something went wrong...${data.message}`
            );
          }
        });
    });
  };

  const updateModel = (model) => {
    gridRef.current.api.applyTransaction({ update: [model] });
  };

  const addModel = (model) => {
    gridRef.current.api.applyTransaction({ add: [model], addIndex: 0 });
  };

  const onRowSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    if (selectedRows.length > 0) {
      setSelectedRowId(selectedRows[0].id);
      const ids = selectedRows.map((row) => row.id);
      setSelectedRowIds(ids);
    } else {
      setSelectedRowId(0);
      setSelectedRowIds([]);
    }
  };

  const handleEditData = (oldData) => {
    console.log(oldData);
  };

  const projectManagers = getProjectManagers();
  const projectManagerProps = {
    options: projectManagers,
    getOptionLabel: (option) => option?.name || "",
  };
  const supplierCategories = getSupplierCategories().filter(
    (x) => x.dependentvalue === "L"
  );
  const suppliers = getSuppliers().filter((x) =>
    supplierCategories.some((y) => y.value === x.dependentvalue)
  );
  const supplierProps = {
    options: suppliers,
    getOptionLabel: (option) => option?.name || "",
  };
  const sites = getSites();
  const siteProps = {
    options: sites,
    getOptionLabel: (option) => option?.name || "",
  };
  const columnDefs = [
    {
      headerName: "Entry Date",
      field: "date",
      checkboxSelection: false,
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
    },
    {
      flex: 1.7,
      headerName: "Contractor Name",
      field: "contractorId",
      autoHeight: true,
      wrapText: true,
      valueGetter: (p) => supplierCellRenderer({ value: p.data.contractorId }),
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      flex: 1.7,
      headerName: "Site",
      field: "siteId",
      autoHeight: true,
      wrapText: true,
      valueGetter: (p) => siteCellRenderer({ value: p.data.siteId }),
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      flex: 1.5,
      headerName: "Project Manager",
      field: "projectManager",
      autoHeight: true,
      wrapText: true,
      valueGetter: (p) =>
        projectManagerCellRenderer({ value: p.data.projectManager }),
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      flex: 0.8,
      headerName: "Pending Amount",
      field: "pendingAmount",
      filter: false,
      floatingFilter: false,
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
    },
    {
      headerName: "Status",
      field: "status",
      valueGetter: (p) => closureStatusRenderer({ value: p.data.status }),
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      headerName: "Modified By",
      field: "modifiedBy",
      valueGetter: (p) => userCellRenderer({ value: p.data.modifiedBy }),
      cellStyle: { "border-right-color": "#e2e2e2" },
      autoHeight: true,
      wrapText: true,
    },
    {
      flex: 1,
      headerName: "Details",
      field: "id",
      sortable: false,
      filter: false,
      floatingFilter: false,
      cellRenderer: (params) => (
        <div style={{ display: "flex" }}>
          {params.data?.status === "P" && (
            <IconButton
              aria-label="edit"
              onClick={() => {
                navigate(
                  PATH_SUB_CONTRACTOR_ADVANCE_REQUEST_DETAIL + params.data.id,
                  {
                    state: {
                      date: dayjs(dateValue).format("YYYY-MM-DD"),
                      isEdit: true,
                    },
                  }
                );
              }}
            >
              <EditIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
            </IconButton>
          )}
          <IconButton
            aria-label="view"
            onClick={() => {
              navigate(
                PATH_SUB_CONTRACTOR_ADVANCE_REQUEST_DETAIL + params.data.id,
                {
                  state: {
                    date: dayjs(dateValue).format("YYYY-MM-DD"),
                    isEdit: false,
                  },
                }
              );
            }}
          >
            <VisibilityIcon style={{ color: PRUSSIAN_BLUE, width: 20 }} />
          </IconButton>

          <IconButton
            aria-label="edit"
            onClick={() => {
              let filename = "";
              let localDto = {};
              const pm = projectManagerProps.options.filter(
                (x) => x.value === params.data.projectManager
              );
              localDto.projectManagerName = pm.length > 0 ? pm[0].name : "";
              const site = siteProps.options.filter(
                (x) => x.value === params.data.siteId
              );
              localDto.siteName = site.length > 0 ? site[0].name : "";
              const supplier = supplierProps.options.filter(
                (x) => x.value === params.data.contractorId
              );
              localDto.contractorName =
                supplier.length > 0 ? supplier[0].name : "";
              localDto.id = params.data.id ? params.data.id : "";
              localDto.siteId = params.data.siteId ? params.data.siteId : "";
              localDto.contractorId = params.data.contractorId
                ? params.data.contractorId
                : "";
              localDto.projectManager = params.data.projectManager
                ? params.data.projectManager
                : "";
              localDto.reasonForAdvance = params.data.reasonForAdvance
                ? params.data.reasonForAdvance
                : "";
              localDto.amountBilledTillDate = params.data.amountBilledTillDate
                ? params.data.amountBilledTillDate
                : 0;
              localDto.retentionAmount = params.data.retentionAmount
                ? params.data.retentionAmount
                : 0;
              localDto.amountRecommended = params.data.amountRecommended
                ? params.data.amountRecommended
                : 0;

              localDto.amountRequested = params.data.amountRequested
                ? params.data.amountRequested
                : 0;
              localDto.numberOfInstallments = params.data.numberOfInstallments
                ? params.data.numberOfInstallments
                : 0;

              localDto.paidAmount = params.data.paidAmount
                ? params.data.paidAmount
                : 0;
              localDto.remark = params.data.remark ? params.data.remark : "";
              localDto.advanceAmountPaid = params.data.advanceAmountPaid
                ? params.data.advanceAmountPaid
                : 0;

              localDto.pendingAmount = params.data.pendingAmount
                ? params.data.pendingAmount
                : 0;
              localDto.status = params.data.status ? params.data.status : "";
              localDto.billListView = params.data.billListView
                ? params.data.billListView
                : [];

              localDto.date = params.data.date
                ? dayjs(params.data.date, "DD-MM-YYYY").format("YYYY-MM-DD")
                : [];

              localDto.retentionBalanceAmount = params.data
                .retentionBalanceAmount
                ? params.data.retentionBalanceAmount
                : 0;

              localDto.testingBalanceAmount = params.data.testingBalanceAmount
                ? params.data.testingBalanceAmount
                : 0;

              APILABOURMGMT.SubContractorAdvanceExport(localDto)
                .then((res) => {
                  const contentType = res.headers.get("content-type");
                  if (contentType.startsWith("application/json")) {
                    return Promise.reject(res.json());
                  } else {
                    const header = res.headers.get("Content-Disposition");
                    if (header !== null) {
                      const parts = header.split(";");
                      filename = parts[1].split("=")[1].replaceAll('"', "");
                    } else {
                      filename = `subcontractorbilling_${params.data.id}.html`;
                    }

                    return res.blob();
                  }
                })
                .then((blob) => {
                  if (blob !== null) {
                    if (localDto.exportExcel) {
                      let url = window.URL.createObjectURL(blob);
                      let a = document.createElement("a");
                      a.href = url;
                      a.download = filename.replace(".html", ".xlsx");
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                    } else {
                      blob.text().then((x) => {
                        let html = x;
                        html = html.replace(
                          "<script>",
                          '<script type="text/javascript">'
                        );
                        let blob = new Blob([html], { type: "text/html" });
                        let url = window.URL.createObjectURL(blob, {
                          oneTimeOnly: true,
                        });

                        let win = window.open(url, "_blank", "");
                        setTimeout(() => {
                          win.print();
                        }, 2000);
                      });
                    }
                  }
                })
                .catch((m) => {
                  setAlertTitleText(
                    "Oops...",
                    `Something went wrong...${m.message}`
                  );
                });
            }}
          >
            <PrintIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
        </div>
      ),
    },
  ];

  const defaultColDef = {
    resizable: true,
    sortable: true,
    editable: false,
    flex: 1,
    filter: "agTextColumnFilter",
    filterParams: {
      caseSensitive: false,
      defaultOption: "startsWith",
    },
    floatingFilter: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  };

  const fetchSubContractorAdvance = (d1, d2) => {
    return APILABOURMGMT.SubContractorAdvances(
      d1.format("YYYY-MM-DD"),
      d2.format("YYYY-MM-DD")
    )
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) {
          setAlertTitleText("Oops..", `An error occurred ${resp.message}`);
          return [];
        }
        return resp.response.sort((a, b) => b.id - a.id);
      });
  };

  const primeLookups = () => {
    APIMASTER.ProjectManagerOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setProjectManagers(r.response));
    APIMASTER.SiteOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setSites(r.response));
    APIMASTER.SupplierCategories()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setSupplierCategories(r.response));
    APIMASTER.UserOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setUsers(r.response));
    APIMASTER.SupplierOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setSuppliers(r.response));
  };

  const onGridReady = async (params) => {
    const d1 = date ? dayjs(date) : dayjs(fromDate || dayjs().startOf("month"));
    const d2 = date
      ? dayjs(toDateValue)
      : dayjs(toDate || dayjs().endOf("month"));

    setDateValue(d1);
    setToDateValue(d2);

    primeLookups();

    const data = await fetchSubContractorAdvance(d1, d2);
    params.api.setRowData(data);

    if (filterModel) params.api.setFilterModel(filterModel);
  };

  const onFilterChanged = (event) => {
    const model = event.api.getFilterModel();
    dispatch(setFilterModel(model));
  };

  return (
    <SideNav
      currentComponent={LABOUR_MANAGEMENT}
      subComponent={SUB_CONTRACTOR_ADVANCE_REQUEST}
      subComponentPath={PATH_SUB_CONTRACTOR_ADVANCE_REQUEST}
      detailComponent=""
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 10,
          marginRight: 10,
        }}
      >
        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
          }}
          onClick={() => {
            navigate(PATH_SUB_CONTRACTOR_ADVANCE_REQUEST_DETAIL + 0, {
              state: {
                date: dayjs(dateValue).format("YYYY-MM-DD"),
                isEdit: false,
              },
            });
          }}
        >
          {ADD}
        </Button>

        <Button
          onClick={deleteModel}
          variant="outlined"
          sx={{ borderColor: SPANISH_GREEN }}
          style={{
            textTransform: "capitalize",
            fontSize: 14,
            color: SPANISH_GREEN,
            minWidth: "180px",
            minHeight: "45px",
            marginRight: 10,
          }}
        >
          {DELETE}
        </Button>
      </div>

      <div
        style={{
          backgroundColor: LIGHT_GREY_BACKGROUND,
          borderRadius: "5px",
          minWidth: "90%",
          minHeight: "50px",
          padding: 20,
          marginBottom: 10,
          borderStyle: "solid",
          border: "1x",
          borderColor: "#eaeaea",
          display: "flex",
        }}
      >
        <div style={{ display: "flex" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="dateId"
              label="From Date"
              value={dateValue}
              onChange={(v) => setDateValue(v)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="toDateId"
              label="To Date"
              value={toDateValue}
              onChange={(v) => setToDateValue(v)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => (
                <TextField {...params} style={{ marginLeft: 10 }} />
              )}
            />
          </LocalizationProvider>
        </div>
        <div
          align="right"
          style={{
            width: "100%",
            right: 0,
          }}
        >
          <Button
            style={{
              backgroundColor: SPANISH_GREEN,
              textTransform: "capitalize",
              fontSize: 14,
              color: WHITE,
              minWidth: "129px",
              minHeight: "45px",
              marginTop: 5,
            }}
            onClick={async () => {
              // save dates in Redux so Back restores them
              dispatch(
                setDates({
                  fromDate: dayjs(dateValue).format("YYYY-MM-DD"),
                  toDate: dayjs(toDateValue).format("YYYY-MM-DD"),
                })
              );

              const data = await fetchSubContractorAdvance(
                dayjs(dateValue),
                dayjs(toDateValue)
              );
              gridRef.current.api.setRowData(data);

              // re-apply saved column filter after refresh
              if (filterModel) gridRef.current.api.setFilterModel(filterModel);
            }}
          >
            {GO}
          </Button>
        </div>
      </div>

      <div className="App">
        <div className="ag-theme-alpine" style={{ height: "570px" }}>
          <AgGridReact
            copyGroupHeadersToClipboard={true}
            enableRangeSelection={true}
            columnDefs={columnDefs}
            ref={gridRef}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            paginationAutoPageSize={true}
            pagination={true}
            rowSelection="multiple"
            getRowId={(p) => p.data.id}
            onSelectionChanged={onRowSelectionChanged}
            onFilterChanged={onFilterChanged}
            suppressBrowserResizeObserver={true}
          />
        </div>
      </div>
    </SideNav>
  );
}
